/**
 * importCalibreStamp — what the bulk importer copies onto a scraped entry once
 * the calibre-check step has matched it to a calibre.
 *
 * Lifted out of BulkImport's `onProceed` closure so it can be tested at all:
 * the importer has no test of its own, and this is the step that decides
 * whether an imported reference carries an authoritative `calibreId` link or
 * only a free-text name.
 *
 * Everything written here lands on the ENTRY, not on the reference document.
 * The underscore-prefixed keys (`_calibreId`, `_calibreData`, `_calibreTierId`,
 * `_calibreTierName`) are the importer's own working fields — `mapEntryToReference`
 * reads them and emits the real reference keys. That indirection is why the
 * dual-write flags are applied in two places rather than one: the spec half is
 * decided here (by whether `_calibreData` is stamped at all), the string half in
 * the mapper (where the scraped fallback is also in scope).
 */
import { detectTierFromCalibreString, tiersForBrand } from './calibreTiers';
import type { CalibreDualWriteFlags } from './calibreBulkUpdate';
import type { CalibreMapEntry } from './ImportCalibreCheck';
import type { ScrapedWatchEntry } from './BulkImport';
import type { CalibreData } from '../../services/firebase';
import type { LineageMemberFields } from '../../services/calibreLineageLogic';

export interface ImportCalibreStampInput {
  entry: ScrapedWatchEntry;
  /** The calibre the calibre-check step matched this entry's string to. */
  calEntry: CalibreMapEntry;
  /** The string that matched — the map key, which may be an alias. */
  matchedCalibre: string;
  /**
   * Where the matched value belongs. `'movement'` comes from an extraction rule
   * that targets the base movement rather than the calibre.
   */
  matchedTarget: 'calibre' | 'movement';
  /** The brand being imported into — scopes tier detection. */
  brandId?: string;
  flags: CalibreDualWriteFlags;
}

/**
 * Copy the matched calibre onto one scraped entry, returning a new entry.
 *
 * Three groups of writes, and they answer to different rules:
 *
 *  1. THE LINK. `_calibreId` is stamped whenever the match carried an id, under
 *     either flag state — it is the migration's destination, not one of the
 *     legacy writes being retired. Absent when the row resolved to a calibre
 *     the check step could not name a document for; the import then writes a
 *     string and no link, exactly as it always did.
 *
 *  2. THE LEGACY SPECS. `_calibreData` and the three flat mirrors
 *     (`movementType`, `jewels`, `powerReserve`) are the calibre's spec copied
 *     into legacy fields, so `flags.legacyMovement` gates them. Withholding
 *     `_calibreData` is all it takes: the mapper reads the calibre DB as the
 *     authoritative source and falls back to the SCRAPED values when it is
 *     absent, so those scraped values survive rather than being replaced by
 *     nothing.
 *
 *  3. THE TIER. Ungated. A tier is not a legacy field, and it is read off the
 *     same entry the link points at, so it can never name a tier of some other
 *     calibre. Detection reads the ORIGINAL scraped text (the tier hint lives
 *     in "Sellita SW200 (Elaboré)", never in the normalized calibre name), so
 *     it runs before the overwrites above touch anything.
 *
 * The legacy calibre STRING is deliberately not decided here — see
 * `mapEntryToReference`, which is where the scraped fallback is also visible.
 */
export function stampCalibreOnEntry({
  entry: source,
  calEntry,
  matchedCalibre,
  matchedTarget,
  brandId,
  flags,
}: ImportCalibreStampInput): ScrapedWatchEntry {
  const calData = calEntry.data;
  // Use normalizedName from lookup when available (e.g., "SW200" → "Sellita SW200-1")
  const calibreName = calEntry.normalizedName || matchedCalibre;

  const entry = { ...source };
  const loose = entry as unknown as Record<string, unknown>;

  if (calibreName) {
    if (matchedTarget === 'movement') {
      // Extraction rule targets base movement only
      if (!entry.movement) entry.movement = calibreName;
    } else {
      // Default: set calibre; set base movement from calibre data (not the calibre name)
      if (!loose.calibre) loose.calibre = calibreName;
      if (!entry.movement && flags.legacyMovement && calData.baseMovement) {
        entry.movement = calData.baseMovement;
      }
    }
  }

  if (calEntry.id) loose._calibreId = calEntry.id;

  if (flags.legacyMovement) {
    // Calibre DB is authoritative — override scraped values for movement fields
    if (calData.movementType) entry.movementType = calData.movementType as string;
    if (calData.jewels) entry.jewels = String(calData.jewels);
    if (calData.powerReserveHours) entry.powerReserve = String(calData.powerReserveHours);
    // Store full calibre data for the mapper to use
    loose._calibreData = calData;
  }

  // The tier. Auto-detected from the raw scraped calibre/movement string
  // (e.g. "Sellita SW200 (Elaboré)") when the resolved calibre has tiers. Read
  // off the SOURCE entry, before any of the overwrites above touched it. Leaves
  // the tier unset when nothing matches.
  //
  // Scoped to the brand being imported into: a shared base movement carries
  // brand-specific tiers for every brand that modifies it, and the scraped text
  // routinely names a sibling reference ("manual version of the SW510"), which
  // is enough for the detector to land on a foreign brand's tier.
  //
  // An EXPLICIT tier on the match short-circuits all of it. That comes from the
  // check step — either by splitting a suffixed string into base + tier ("Ball
  // RR1102-CSL"), or from the curator's own pick in that step's tier selector.
  // Both are stronger readings of the same text than anything inferred here:
  // the split already required the base to resolve and the tier to exist on it,
  // and either was open to the curator to change. Re-deriving from the raw text
  // could only agree or be wrong.
  const scopedTiers = tiersForBrand(calData.tiers, brandId);
  if (calEntry.tierCleared) {
    // The curator picked "— No tier —" in the check step. Nothing is stamped,
    // and — the whole point of the flag — the text detector below does NOT get
    // to put back what they just took off. Without it, clearing a tier on a row
    // whose scraped string still says "COSC" would silently re-derive it, and
    // the control would look broken in the one case it exists for.
  } else if (calEntry.tierId) {
    loose._calibreTierId = calEntry.tierId;
    loose._calibreTierName =
      calEntry.tierName ?? scopedTiers.find((t) => t.id === calEntry.tierId)?.name;
  } else if (scopedTiers.length > 0) {
    const rawCalibreText = [
      (source as unknown as Record<string, unknown>).calibre,
      source.movement,
    ]
      .filter(Boolean)
      .map(String)
      .join(' ');
    const detectedTier = detectTierFromCalibreString(rawCalibreText, scopedTiers);
    if (detectedTier) {
      const tierName = scopedTiers.find((t) => t.id === detectedTier.tierId)?.name;
      loose._calibreTierId = detectedTier.tierId;
      loose._calibreTierName = tierName;
      if (import.meta.env.DEV) {
        console.debug(
          `[bulkImport] Auto-detected tier '${tierName}' from calibre string '${rawCalibreText}' → set calibreTierId`,
        );
      }
    }
  }

  return entry;
}

/** The calibre-check stamp's working keys — the link and everything derived from it. */
const CALIBRE_LINK_KEYS = ['_calibreId', '_calibreData', '_calibreTierId', '_calibreTierName'] as const;

/** Case/spacing/dash-insensitive comparison for "is this still the same calibre name". */
function sameCalibreName(a: unknown, b: unknown): boolean {
  const norm = (v: unknown) =>
    String(v ?? '')
      .normalize('NFC')
      .toLowerCase()
      .replace(/[‐-―−]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
  return norm(a) !== '' && norm(a) === norm(b);
}

/**
 * The curator typed a calibre on the preview step.
 *
 * The typed string becomes the entry's explicit `calibre` (and `movement`, the
 * field the input shows and the calibre check matches on), WHOLE. The write
 * path prefers `entry.calibre`; without it, it fell back to
 * `extractCalibre(movement)`, which only keeps a letters-then-digits code —
 * "Miyota 9039", "Valjoux 7750", "ETA 2824-2" extract nothing, so a typed
 * calibre that matched no calibre doc was silently dropped, and one that did
 * extract was cut down ("Sellita SW200-1" → "SW200").
 *
 * If the row had been linked to a calibre doc at the calibre-check step and
 * the curator changed the NAME (not just its case or spacing), the link and
 * everything derived from it no longer describe this row: they are dropped, so
 * the reference lands with the typed name and no `calibreId` — "has a calibre
 * name, needs a calibre-doc match", fixable from the reference listing —
 * instead of the typed name beside a stale link (or, before this, the old
 * canonical name, since a matched row's `calibre` was never updated).
 *
 * An empty value clears the calibre and the link.
 */
export function applyManualCalibreEdit(source: ScrapedWatchEntry, value: string): ScrapedWatchEntry {
  const entry = { ...source };
  const loose = entry as unknown as Record<string, unknown>;
  const previous = loose.calibre ?? source.movement;
  const trimmed = value.trim();
  // Keep exactly what was typed in the input while editing (trailing spaces
  // included); the trimmed value is what the write uses.
  entry.movement = value;
  if (trimmed) loose.calibre = trimmed;
  else delete loose.calibre;
  if (!trimmed || !sameCalibreName(trimmed, previous)) {
    for (const key of CALIBRE_LINK_KEYS) delete loose[key];
  }
  return entry;
}

/** Calibre CODE inside scraped movement prose, e.g. "Seiko VK63 Meca-Quartz" → "VK63". */
export function extractCalibre(movement: string): string | undefined {
  if (!movement) return undefined;
  const match = movement.match(/\b([A-Z]{1,3}\d{2,4}[A-Z0-9]*)\b/);
  return match?.[1] ?? undefined;
}

/**
 * The calibre fields an imported reference is written with.
 *
 *  - `calibre` (free text): the entry's explicit calibre NAME when it has one
 *    (calibre-check canonical name, curator edit, AI enrich) — kept whole —
 *    else the code extracted from scraped `movement` prose.
 *  - `calibreId`: the link stamped at the calibre-check step, if any.
 *
 * An unlinked row ALWAYS keeps its calibre string: an import is create-only,
 * so dropping it would leave the reference with neither a link nor a name.
 * `legacyCalibreString` only decides whether a LINKED row also keeps it.
 * `calibreName` is the name either way (for search text).
 */
export function importCalibreFields(
  entry: ScrapedWatchEntry,
  flags: Pick<CalibreDualWriteFlags, 'legacyCalibreString'>,
): { calibre: string | undefined; calibreId: string | undefined; calibreName: string | undefined } {
  const loose = entry as unknown as Record<string, unknown>;
  const explicit = typeof entry.calibre === 'string' ? entry.calibre.trim() : '';
  const calibre = explicit || (entry.movement ? extractCalibre(entry.movement) : undefined);
  const calibreId = (loose._calibreId as string) || undefined;
  return {
    calibre: calibreId && !flags.legacyCalibreString ? undefined : calibre,
    calibreId,
    calibreName: calibre,
  };
}

/** A member of the matched calibre's revision line, with its specs (bulk-import revision resolver). */
export type RevisionLineMember = LineageMemberFields & { data: CalibreData };

const LEGACY_SPEC_MIRRORS: ReadonlyArray<[keyof ScrapedWatchEntry, keyof CalibreData]> = [
  ['movementType', 'movementType'],
  ['jewels', 'jewels'],
  ['powerReserve', 'powerReserveHours'],
];

/**
 * Re-point an already-stamped entry from one revision of a line to another
 * (the resolver's pick, or the curator's on the preview grid). Rewrites only
 * what the stamp derived from the calibre:
 *  - `_calibreId` → the new revision;
 *  - `calibre` → the new name, but only when it still holds the old matched
 *    name (or nothing) — a scraped or typed string is left alone;
 *  - legacy spec mirrors (flag-gated, as in the stamp) → the new revision's,
 *    clearing a mirror that came from the old revision when the new one has
 *    no value (2824: 17 jewels → 2824-2: 25);
 *  - the tier → kept when the new revision has a tier of the same name for
 *    this brand, otherwise dropped (tier ids belong to one calibre).
 */
export function applyRevisionPick({
  entry: source,
  from,
  to,
  brandId,
  flags,
}: {
  entry: ScrapedWatchEntry;
  from: { name: string; data?: CalibreData };
  to: RevisionLineMember;
  brandId?: string;
  flags: CalibreDualWriteFlags;
}): ScrapedWatchEntry {
  const entry = { ...source };
  const loose = entry as unknown as Record<string, unknown>;
  loose._calibreId = to.id;
  if (!loose.calibre || sameCalibreName(loose.calibre, from.name)) loose.calibre = to.name;
  if (flags.legacyMovement) {
    for (const [entryKey, dataKey] of LEGACY_SPEC_MIRRORS) {
      const next = to.data[dataKey];
      const prev = from.data?.[dataKey];
      if (next !== undefined && next !== null && next !== '') {
        loose[entryKey] = entryKey === 'movementType' ? next : String(next);
      } else if (prev !== undefined && String(loose[entryKey] ?? '') === String(prev)) {
        delete loose[entryKey];
      }
    }
    loose._calibreData = to.data;
  }
  const tierName = loose._calibreTierName;
  const sameTier =
    typeof tierName === 'string' ? tiersForBrand(to.data.tiers, brandId).find((t) => t.name === tierName) : undefined;
  if (sameTier) {
    loose._calibreTierId = sameTier.id;
    loose._calibreTierName = sameTier.name;
  } else {
    delete loose._calibreTierId;
    delete loose._calibreTierName;
  }
  return entry;
}
